/* ═══════════════════════════════════════════════════════════════════════════════
   Variations Page Tour (v2.3.0)

   Feature update tour highlighting:
   - Variations ↔ Install & Hire sub-tab toggle
   - Status summary cards (Valid / Invalid / Pending)
   - Inline status filter chips
   - Status-coded variation rows
   - Install & Hire BETA tab
   ═══════════════════════════════════════════════════════════════════════════════ */

import type { TourDefinition } from './types';

export const variationsPageTour: TourDefinition = {
  id: 'variations_page_v2_3',
  version: '2.3.0',
  type: 'feature_update',
  audience: 'all',
  page: 'project_detail',
  priority: 1,
  title: 'Variations Tab Update',
  subtitle: 'Status filtering, smarter rows, and Install & Hire tracking.',

  steps: [
    // ── Step 0: Welcome ──────────────────────────────────────────────
    {
      id: 'welcome',
      type: 'welcome',
      title: 'What\'s New in Variations',
      description: 'We\'ve added status-based filtering, visual status indicators, and a brand-new Install & Hire register. Here\'s a quick walkthrough.',
      icon: '✨',
      actionLabel: 'Show Me',
    },

    // ── Step 1: Sub-Tab Toggle ───────────────────────────────────────
    {
      id: 'subtab-toggle',
      type: 'spotlight',
      targetSelector: '[data-tour-id="var-subtab-toggle"]',
      position: 'bottom',
      title: 'Variations & Install \u0026 Hire',
      description: 'Switch between the Variations register and the new Install & Hire register. Variations tracks all your Dashpivot dockets. Install & Hire tracks scaffolding, formwork, and equipment hire from Dashpivot.',
      icon: '🔀',
      actionLabel: 'Next',
      spotlightPadding: 8,
    },

    // ── Step 2: Status Summary Cards ─────────────────────────────────
    {
      id: 'status-cards',
      type: 'spotlight',
      targetSelector: '[data-tour-id="var-status-cards"]',
      position: 'bottom',
      title: 'Status at a Glance',
      description: 'See how many variations are Valid, Invalid, or Pending, plus the total value of valid variations. Click any card to filter the list instantly.',
      icon: '📊',
      actionLabel: 'Next',
      spotlightPadding: 10,
    },

    // ── Step 3: Status Filter Chips ──────────────────────────────────
    {
      id: 'filter-chips',
      type: 'spotlight',
      targetSelector: '[data-tour-id="var-filter-chips"]',
      position: 'bottom',
      title: 'Quick Status Filters',
      description: 'Filter variations by status inline, which works alongside your date and docket filters. The coloured dots match each status.',
      icon: '🏷️',
      actionLabel: 'Next',
      spotlightPadding: 8,
    },

    // ── Step 4: Variation Row Styling ────────────────────────────────
    {
      id: 'row-styling',
      type: 'spotlight',
      targetSelector: '[data-tour-id="var-first-row"]',
      position: 'bottom',
      title: 'Status-Coded Rows',
      description: 'Each variation row is now colour-coded: green for Valid, red for Invalid, and amber for Pending. You can spot the status without opening the detail.',
      icon: '🎨',
      actionLabel: 'Next',
      spotlightPadding: 4,
    },

    // ── Step 5: Install & Hire BETA ──────────────────────────────────
    {
      id: 'install-hire-beta',
      type: 'spotlight',
      targetSelector: '[data-tour-id="var-install-hire-tab"]',
      position: 'bottom',
      title: 'Install \u0026 Hire (BETA)',
      description: 'We\'re building Install & Hire to track scaffolding, formwork, and equipment hire items synced from Dashpivot. It\'s currently in BETA while we gather feedback on how it should work for your BAU processes. Give it a try and let us know!',
      icon: '🔧',
      actionLabel: 'Got It',
      spotlightPadding: 8,
    },

    // ── Step 6: Completion ───────────────────────────────────────────
    {
      id: 'completion',
      type: 'completion',
      title: 'You\'re All Set!',
      description: 'Use status cards and filters to quickly find the variations that matter. Try out Install & Hire and share your feedback.',
      icon: '🎉',
      actionLabel: 'Start Exploring',
    },
  ],
};
